import { Dispatch, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import classNames from 'classnames';
import { Action } from 'redux';

import { CallStatus, CurrencyEnum } from '../../../lib/enums';
import lang from '../../../lib/language';
import { CountryData } from '../../../lib/models/enroll';
import { RootState } from '../../../lib/store';
import { refreshPayments } from '../../../lib/store/common-actions';
import { setBankAccountDetailsOnCreate } from '../../../lib/store/crm';
import { setPaymentCallStatusToInitial } from '../../../lib/store/payment';
import { PaymentCall } from '../../../lib/store/payment/types';
import CountriesCache from '../../../lib/store-util/CountriesCache';
import {
  getBankNameValidity,
  getBicSwiftValidity,
  getIbanValidity,
} from '../../../lib/util/ValidationSchemes/Validate';

import iconExpandDownWhite from '../../../assets/img/icon-expand-more-down-white.svg';
import { useBrowserWidth, useIsMobile } from '../../../hooks';
import Button from '../../../shared_components/Button/Button';
import CommonDropdown from '../../../shared_components/CommonDropdown/CommonDropdown';
import { UI_ROUTES } from '../../../util/routes';
import {
  BaseModal,
  CountryControl,
  InputControl,
  LogoAndCloseButton,
} from '../..';
import ContainerBackButton from '../../ContainerBackButton/ContainerBackButton';
import { ICountryControlOptions } from '../../CountryControl/CountryControl.types';
import GoBackButton from '../../GoBackButton/GoBackButton';
import AddBankAccountModal from '../../PayoutsModals/AddBankAccountModal';
import { IOption } from '../../SelectControl/SelectControl.types';

import { BankAccountForm, DefaultBankAccountFormValue } from './AddYourBankAccount.constants';
import { ValidateForm } from './ValidateForm';

import styles from './AddYourBankAccount.module.scss';

type Props = {} & ReduxProps;

const AddYourBankAccount = (props: Props) => {
  const {
    individual,
    callsForRefresh,
    bankAccountDetailsOnCreate,
    requestStatusRegisterLinkedAccount,
    setBankAccountValuesOnCreate,
    setPaymentStatusByCall,
    refreshPaymentsData,
  } = props;

  const { isMobile } = useIsMobile();
  const { browserWidthSize } = useBrowserWidth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { previousPage?: string };
  const addYourBankAccountPath = UI_ROUTES.payouts + UI_ROUTES.addYourBankAccount;
  const isDefaultGoBackButton = browserWidthSize >= 989 && !isMobile;
  const [isInitialised, setIsInitialised] = useState<boolean>(false);
  const [isClickedBtnContinue, setIsClickedBtnContinue] = useState<boolean>(false);
  const [formData, setFormData] = useState<BankAccountForm>(DefaultBankAccountFormValue);
  const [showModalAddBankAccount, setShowModalAddBankAccount] = useState<boolean>(false);
  const excludedKeys = ['amountValue', 'file_storage_id'];

  const isBtnDisabled = Object.entries(formData)
    .filter(([key]) => !excludedKeys.includes(key))
    .some(([, value]) => value.toString().trim() === '');

  const currencies = useMemo(
    (): IOption[] => [
      { value: CurrencyEnum.USD, label: CurrencyEnum.USD },
      { value: CurrencyEnum.EUR, label: CurrencyEnum.EUR },
    ],
    [],
  );

  const countryList = CountriesCache.use();
  const countryOptions = useMemo((): ICountryControlOptions[] => {
    if (countryList && countryList.length > 0) {
      return countryList
        .filter((country: CountryData) => country.id && country.name)
        .sort((current: CountryData, next: CountryData) => {
          if (current.name.toLowerCase() < next.name.toLowerCase()) {
            return -1;
          }
          if (current.name.toLowerCase() > next.name.toLowerCase()) {
            return 1;
          }
          return 0;
        })
        .map((country: CountryData) => ({
          label: country.name,
          value: country.id,
        }));
    }
    return [];
  }, [countryList]);

  useEffect(() => {
    if (!isInitialised && individual) {
      const updatedFormData: BankAccountForm = {
        ...formData,
        firstName: individual!.first_name,
        lastName: individual!.last_name,
        bank_name: bankAccountDetailsOnCreate.account_info.bank_name || '',
        bank_country_id:
          bankAccountDetailsOnCreate.account_info.bank_country_id
          || individual!.addresses[0].country_id,
        bic: bankAccountDetailsOnCreate.account_info.bic || '',
        iban: bankAccountDetailsOnCreate.account_info.iban || '',
        currency_code: currencies[0].value,
      };
      setFormData(updatedFormData);
      setIsInitialised(true);
      setPaymentStatusByCall();
    }
  }, [
    isInitialised,
    formData,
    individual,
    currencies,
    bankAccountDetailsOnCreate,
    setPaymentStatusByCall,
  ]);

  useEffect(() => {
    if (callsForRefresh) refreshPaymentsData();
  }, [callsForRefresh, refreshPaymentsData]);

  useEffect(() => {
    if (requestStatusRegisterLinkedAccount && isInitialised) {
      if (requestStatusRegisterLinkedAccount.status === CallStatus.ERROR) {
        navigate(
          {
            pathname: UI_ROUTES.error,
          },
          {
            state: {
              callName: requestStatusRegisterLinkedAccount.callName,
              errorCode: requestStatusRegisterLinkedAccount.errorCode,
            },
          },
        );
        setShowModalAddBankAccount(false);
      } else if (requestStatusRegisterLinkedAccount.status === CallStatus.READY) {
        setShowModalAddBankAccount(true);
      }
    }
  }, [navigate, isInitialised, requestStatusRegisterLinkedAccount]);

  const handleUploadStatement = () => {
    setIsClickedBtnContinue(true);
    if (ValidateForm(formData.bank_name, formData.bic, formData.iban)) {
      setBankAccountValuesOnCreate({
        bank_name: formData.bank_name,
        bank_country_id: formData.bank_country_id,
        bic: formData.bic,
        iban: formData.iban,
        currency_code: formData.currency_code,
      });

      navigate(UI_ROUTES.payouts + UI_ROUTES.uploadBankStatement, {
        state: {
          previousPage: addYourBankAccountPath,
        },
      });
    }
  };

  const handleBack = () => {
    setBankAccountValuesOnCreate({});
    // If previousPage is explicitly set, use it
    if (locationState?.previousPage) {
      return navigate(locationState.previousPage);
    }
    // Otherwise go to payouts methods
    return navigate(UI_ROUTES.payouts + UI_ROUTES.payoutsMethods);
  };

  const handleClose = () => {
    setBankAccountValuesOnCreate({});
    return navigate(UI_ROUTES.payouts);
  };

  return (
    <>
      <div className={styles.wrapperContainer}>
        <LogoAndCloseButton
          displayCloseButton={!isMobile}
          displayBackButton={isDefaultGoBackButton}
          onBackBtnPress={handleBack}
          onClose={handleClose}
          newWebGoBackButtonDesign
        />
        {!isDefaultGoBackButton && !isMobile && (
          <div className={styles.goBackButtonContainer}>
            <ContainerBackButton onBackBtnPress={handleBack} />
          </div>
        )}
        {isMobile && (
          <div className={styles.mobileBackContainer}>
            <GoBackButton onBackBtnPress={handleBack} />
          </div>
        )}
        <div className={styles.containerContent}>
          {isMobile && (
            <div className={styles.titleContainer}>
              <h3 className={styles.title} id="addBankAccountTitle">
                {lang.mobileAddBankAccTitle()}
              </h3>
              <span className={styles.description}>{lang.mobileAddBankAccSubtitle()}</span>
            </div>
          )}
          <div
            className={classNames([
              styles.wrapperContent,
              { [styles.mobileWrapperContent]: isMobile },
            ])}
          >
            {!isMobile && (
              <div>
                <h3 className={styles.title} id="addBankAccountTitle">
                  {lang.mobileAddBankAccTitle()}
                </h3>
                <span className={styles.description}>{lang.mobileAddBankAccSubtitle()}</span>
              </div>
            )}
            <div className={styles.namesWrapper}>
              <div className={styles.line}>
                <p>{lang.commonOnboardingPersonalDetailsFirstName()}</p>
                <p>{formData.firstName}</p>
              </div>
              <div className={styles.line}>
                <p>{lang.commonOnboardingPersonalDetailsLastName()}</p>
                <p>{formData.lastName}</p>
              </div>
            </div>
            <div className={styles.inputContainer}>
              <p className={classNames([styles.inputTitle, styles.firstInputTitle])}>
                {lang.mobileAddBankAccInputBank()}
              </p>
              <InputControl
                id="addBankAccountBankName"
                name=""
                placeholder={lang.mobileAddBankAccPlaceholderBank()}
                className={classNames([
                  styles.input,
                  { [styles.inputError]: isClickedBtnContinue },
                ])}
                extraStyles
                label=""
                value={formData.bank_name}
                onValueChange={(value: string | number) => {
                  setFormData((currentState: BankAccountForm) => ({
                    ...currentState,
                    bank_name: value as string,
                  }));
                }}
                errors={isClickedBtnContinue ? getBankNameValidity(formData.bank_name).errors : []}
              />
            </div>
            <div className={styles.bankCountryContainer}>
              <p className={styles.inputTitle}>{lang.mobileAddBankAccInputBankCountry()}</p>
              <CountryControl
                id="addBankAccountCountry"
                options={countryOptions}
                value={formData.bank_country_id}
                ariaLabel="Bank Country"
                placeholder=""
                icon={iconExpandDownWhite}
                onCountryChange={(countryId: string) => {
                  setFormData((currentState: BankAccountForm) => ({
                    ...currentState,
                    bank_country_id: countryId,
                  }));
                }}
              />
            </div>
            <div className={styles.inputContainer}>
              <p className={styles.inputTitle}>{lang.mobileAddBankAccInputBicSwift()}</p>
              <InputControl
                id="addBankAccountBicCode"
                name=""
                placeholder={lang.mobileAddBankAccPlaceholderIBAN()}
                className={classNames([
                  styles.input,
                  { [styles.inputError]: isClickedBtnContinue },
                ])}
                extraStyles
                label=""
                value={formData.bic}
                onValueChange={(value: string | number) => {
                  setFormData((currentState: BankAccountForm) => ({
                    ...currentState,
                    bic: value as string,
                  }));
                }}
                errors={isClickedBtnContinue ? getBicSwiftValidity(formData.bic).errors : []}
              />
            </div>
            <div className={styles.inputContainer}>
              <p className={styles.inputTitle}>{lang.mobileAddBankAccInputIBAN()}</p>
              <InputControl
                id="addBankAccountIBAN"
                name=""
                placeholder={lang.mobileAddBankAccPlaceholderBicSwift()}
                className={classNames([
                  styles.input,
                  { [styles.inputError]: isClickedBtnContinue },
                ])}
                extraStyles
                label=""
                value={formData.iban}
                onValueChange={(value: string | number) => {
                  setFormData((currentState: BankAccountForm) => ({
                    ...currentState,
                    iban: value as string,
                  }));
                }}
                errors={isClickedBtnContinue ? getIbanValidity(formData.iban).errors : []}
              />
            </div>
            <div className={styles.inputContainer}>
              <p className={classNames(styles.currency, { [styles.mobileCurrency]: isMobile })}>
                {lang.mobileAddBankAccInputCurrency()}
              </p>
              <CommonDropdown
                id="addBankAccountCurrency"
                title={lang.mobileAddBankAccInputCurrency()}
                options={currencies.map((currency: IOption) => ({
                  id: currency.value.toString(),
                  label: currency.label,
                }))}
                selectedOption={{
                  id: formData.currency_code.toString(),
                  label: formData.currency_code.toString(),
                }}
                onSelectOption={(option) => {
                  setFormData((currentState: BankAccountForm) => ({
                    ...currentState,
                    currency_code: option.id,
                  }));
                }}
              />
            </div>
            {!isMobile && (
              <Button
                disabled={isBtnDisabled}
                className={styles.uploadBtn}
                id="addBankAccountUpload"
                fullWidth
                onClick={handleUploadStatement}
                biggerFontSize
              >
                {lang.commonContinueButton()}
              </Button>
            )}
          </div>
          {isMobile && (
            <div className={styles.uploadBtnWrapper}>
              <Button
                disabled={isBtnDisabled}
                className={styles.uploadBtn}
                id="addBankAccountUpload"
                fullWidth
                onClick={handleUploadStatement}
              >
                {lang.commonContinueButton()}
              </Button>
            </div>
          )}
        </div>
      </div>
      <BaseModal isOpen={showModalAddBankAccount}>
        {showModalAddBankAccount && (
          <AddBankAccountModal
            handleContinue={handleClose}
            textInfoModal={{
              title: lang.mobileBankDetailsSetTitle(),
              description1: lang.webPopupBankDetailsDescription1(),
              description2: lang.webPopupBankDetailsDescription2(),
            }}
          />
        )}
      </BaseModal>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  individual: state.crm.individualExtendedInfo,
  callsForRefresh: state.payment.callsForRefresh,
  bankAccountDetailsOnCreate: state.crm.enroll.bankAccountDetailsOnCreate,
  requestStatusRegisterLinkedAccount: state.payment.statusByCall[PaymentCall.registerLinkedAccount],
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  refreshPaymentsData: () => dispatch(refreshPayments()),
  setBankAccountValuesOnCreate: (data: any) => dispatch(setBankAccountDetailsOnCreate(data)),
  setPaymentStatusByCall: () => dispatch(setPaymentCallStatusToInitial(null)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(AddYourBankAccount);
